import LFCButton from '_components/inputs/LFCButton';
import LFCSelectForm from '_components/inputs/LFCSelectForm';
import LFCTextField from '_components/inputs/LFCTextField';
import LFCFormRowGroup from '_components/layout/LFCFormRowGroup';
import LFCTitledCard from '_components/surfaces/LFCTitledCard';
import {useAuthUser, useRegetAuthUser} from '_contexts/AuthUserProvider';
import {useMySettings, useSaveSetMySetting} from '_contexts/MySettingsProvider';
import {adminAPIAccess, handleInputChange} from '_logics/LFCUtil';
import {CognitoUserAttribute} from 'amazon-cognito-identity-js';
import awsConfiguration from 'awsConfiguration';
import Decimal from 'decimal.js-light';
import {useSnackbar} from 'notistack';
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Chip, FormControlLabel, Grid, Radio, RadioGroup} from '@mui/material';

/**
 * 個人設定画面
 * @returns
 */
const LFCMySettingPage = () => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const mySettings = useMySettings();
  const authUser = useAuthUser();
  const regetAuthUser = useRegetAuthUser();
  const saveMySetting = useSaveSetMySetting();
  const formEmailRef = useRef<HTMLFormElement>(null!);

  const [values, setValues] = useState({
    emailAddr: '',
    nowPass: '',
    newPass1: '',
    newPass2: ''
  });

  // 丸めモードの選択肢（LFCSelectForm用）
  const roundingModeOptions = [
    {value: Decimal.ROUND_UP, name: t('切り上げ')},
    {value: Decimal.ROUND_DOWN, name: t('切り捨て')},
    {value: Decimal.ROUND_HALF_UP, name: t('四捨五入')}
  ];

  /**
   * テーマ切替
   * @param isOpen
   */
  const themeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    saveMySetting({...mySettings, isDark: target.value === true.toString()});
  };

  /**
   * locale切替
   * @param isOpen
   */
  const localeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    saveMySetting({...mySettings, locale: target.value});
  };

  /**
   * 数値精度設定変更
   */
  const handlePrecisionChange = (field: string, value: number) => {
    saveMySetting({...mySettings, [field]: value});
  };

  /**
   * 丸めモード設定変更
   */
  const handleRoundingModeChange = (field: string, value: number) => {
    saveMySetting({...mySettings, [field]: value});
  };

  /**
   * Email保存
   * @param isOpen
   */
  const saveEmail = () => {
    // input check
    if (!formEmailRef.current?.reportValidity()) {
      return;
    }

    // cognitoに登録
    adminAPIAccess({
      apipath: 'UpdateUserAttributes',
      parameters: {
        user_pool_id: awsConfiguration.UserPoolId,
        user_attributes: [
          new CognitoUserAttribute({
            Name: 'email',
            Value: values.emailAddr
          }),
          new CognitoUserAttribute({
            Name: 'email_verified',
            Value: 'true'
          })
        ]
      }
    })
      .then(response => {
        regetAuthUser();
        enqueueSnackbar(t('message.更新しました'), {variant: 'success'});
      })
      .catch(error => {
        console.log(error);
        if (error.response.status === 401) {
          // 認証エラー
          enqueueSnackbar(
            t('message.セッションの有効期限が切れました。再度ログインしてください。'),
            {variant: 'error'}
          );
        } else {
          enqueueSnackbar(`${t('message.更新失敗しました')}:${error}`, {
            variant: 'error',
            persist: true
          });
        }
      });
  };

  useEffect(() => {
    setValues({...values, emailAddr: authUser?.email ?? ''});
  }, [authUser]);

  return (
    <>
      <Grid container alignItems="center">
        <Grid item xs={12}>
          <LFCTitledCard title={t('テーマ')} transparent>
            <RadioGroup name="theme" value={mySettings.isDark} onChange={themeChange} row>
              <FormControlLabel value={false} control={<Radio />} label="Light" />
              <FormControlLabel value={true} control={<Radio />} label="Dark" />
            </RadioGroup>
          </LFCTitledCard>
        </Grid>
        <Grid item xs={12}>
          <LFCTitledCard title={t('表示言語')} transparent>
            <RadioGroup
              name="locale"
              value={mySettings.locale?.toLowerCase() || 'ja'}
              onChange={localeChange}
              row
            >
              <FormControlLabel value={'ja'} control={<Radio />} label="日本語" />
              <FormControlLabel value={'en'} control={<Radio />} label="English" />
            </RadioGroup>
          </LFCTitledCard>
        </Grid>
        <Grid item xs={12}>
          <LFCTitledCard title={t('数値表示')} transparent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Grid container spacing={1} alignItems="center">
                  {/* パーセント設定 */}
                  <Grid
                    item
                    xs={12}
                    md={3}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Chip label={t('パーセント')} sx={{width: '100%'}} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <LFCTextField
                      name="percentPrecision"
                      label={t('小数点以下桁数')}
                      value={mySettings.percentPrecision ?? 3}
                      onChange={event => {
                        if (event) {
                          const value = parseInt(event.target.value, 10);
                          if (!isNaN(value) && value >= 0 && value <= 3) {
                            handlePrecisionChange('percentPrecision', value);
                          }
                        }
                      }}
                      other={{
                        type: 'number',
                        inputProps: {min: 0, max: 3}
                      }}
                      size="medium"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <LFCSelectForm
                      name="percentRoundingMode"
                      label={t('端数処理')}
                      value={mySettings.percentRoundingMode ?? Decimal.ROUND_UP}
                      onChange={event => {
                        handleRoundingModeChange('percentRoundingMode', Number(event.target.value));
                      }}
                      selectItem={roundingModeOptions}
                      id="percentRoundingMode"
                      fullWidth
                      size="medium"
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={6}>
                {/* サイズ設定 */}
                <Grid container spacing={1} alignItems="center">
                  <Grid
                    item
                    xs={12}
                    md={3}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Chip label={t('サイズ')} sx={{width: '100%'}} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <LFCTextField
                      name="sizePrecision"
                      label={t('小数点以下桁数')}
                      value={mySettings.sizePrecision ?? 3}
                      onChange={event => {
                        if (event) {
                          const value = parseInt(event.target.value, 10);
                          if (!isNaN(value) && value >= 0 && value <= 3) {
                            handlePrecisionChange('sizePrecision', value);
                          }
                        }
                      }}
                      other={{
                        type: 'number',
                        inputProps: {min: 0, max: 3}
                      }}
                      size="medium"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <LFCSelectForm
                      name="sizeRoundingMode"
                      label={t('端数処理')}
                      value={mySettings.sizeRoundingMode ?? Decimal.ROUND_UP}
                      onChange={event => {
                        handleRoundingModeChange('sizeRoundingMode', Number(event.target.value));
                      }}
                      selectItem={roundingModeOptions}
                      id="sizeRoundingMode"
                      fullWidth
                      size="medium"
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={6}>
                <Grid container spacing={1} alignItems="center">
                  <Grid
                    item
                    xs={12}
                    md={3}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Chip label={t('その他')} sx={{width: '100%'}} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <LFCTextField
                      name="defaultPrecision"
                      label={t('小数点以下桁数')}
                      value={mySettings.defaultPrecision ?? 3}
                      onChange={event => {
                        if (event) {
                          const value = parseInt(event.target.value, 10);
                          if (!isNaN(value) && value >= 0 && value <= 3) {
                            handlePrecisionChange('defaultPrecision', value);
                          }
                        }
                      }}
                      other={{
                        type: 'number',
                        inputProps: {min: 0, max: 3}
                      }}
                      size="medium"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <LFCSelectForm
                      name="defaultRoundingMode"
                      label={t('端数処理')}
                      value={mySettings.defaultRoundingMode ?? Decimal.ROUND_UP}
                      onChange={event => {
                        handleRoundingModeChange('defaultRoundingMode', Number(event.target.value));
                      }}
                      selectItem={roundingModeOptions}
                      id="defaultRoundingMode"
                      fullWidth
                      size="medium"
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={0} md={6}></Grid>
            </Grid>
          </LFCTitledCard>
        </Grid>
        <Grid item xs={12}>
          <LFCTitledCard title={t('ユーザ情報')} transparent>
            <form ref={formEmailRef}>
              <LFCFormRowGroup>
                <LFCTextField
                  name="emailAddr"
                  label="Email"
                  value={values.emailAddr}
                  onChange={event => {
                    handleInputChange(event, values, setValues);
                  }}
                  other={{
                    required: true,
                    inputProps: {
                      pattern: '^[a-zA-Z0-9._%\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$'
                    }
                  }}
                  sx={{width: '300px'}}
                />
                <LFCButton color="primary" onClick={saveEmail}>
                  {t('更新')}
                </LFCButton>
              </LFCFormRowGroup>
            </form>
          </LFCTitledCard>
        </Grid>
      </Grid>
    </>
  );
};

export default LFCMySettingPage;
